import React, { FC } from 'react';
import styled, { css } from 'styled-components/macro';
import cx from 'classnames';
import { StaticSplashThumbnail } from 'src/@types/StaticSplashResponse';
import { useSelector } from 'src/client/redux';
import { NavLink } from 'react-router-dom';
import { DESKTOP, XX_LARGE } from 'src/client/constants/mediaQueries';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { useJoinUrl } from 'src/client/components/buttons/JoinButton';
import { faCamera, faFilm } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type Props = {
  className?: string;
  thumbnail: StaticSplashThumbnail;
  type: 'movies' | 'models' | 'galleries' | 'classics';
};

const icons = {
  movies: faFilm,
  galleries: faCamera,
  classics: faCamera,
};

export const Thumbnail = (props: Props) => {
  const { thumbnail, type } = props;

  const { cdnUrl } = useSelector((state) => state.app);
  const site = useSelector((state) => state.site);
  const showSfwSite = useShowSfwSite();

  const nude = showSfwSite ? 'nonNude' : 'nude';
  const makeThumbnailUrl = (thumbnailUUID: string) => {
    return `${cdnUrl}/staticSplash/${site.UUID}/${type}/${nude}/${thumbnailUUID}.jpg`;
  };

  return (
    <WrapperComponent
      type={type}
      className={cx('thumbnail', props.className, { isFeatured: thumbnail.isFeatured })}
      path={thumbnail.path}
    >
      <BackgroundImage src={makeThumbnailUrl(thumbnail.UUID)} alt={thumbnail.title} />
      <Description>
        <Title>{thumbnail.title}</Title>
        {thumbnail.modelName && <ModelName>{thumbnail.modelName}</ModelName>}
        {thumbnail.mediaCount && (
          <MediaCount>
            (<StyledIcon icon={icons[type]} /> {thumbnail.mediaCount})
          </MediaCount>
        )}
      </Description>
    </WrapperComponent>
  );
};

const WrapperComponent: FC<{ path?: string; className?: string; type: string }> = (props) => {
  const [joinUrl] = useJoinUrl({ campaign: `static-splash-${props.type}` });

  if (props.path) {
    return (
      <Wrapper to={props.path} className={props.className}>
        {props.children}
      </Wrapper>
    );
  }

  return (
    <Wrapper as="a" href={joinUrl} className={props.className}>
      {props.children}
    </Wrapper>
  );
};

const Wrapper = styled(NavLink)<{ isFeatured?: boolean }>`
  grid-column: span 1;
  grid-row: span 1;
  position: relative;
  overflow: hidden;
  width: 100%;
  display: block;

  ${(props) =>
    props.isFeatured &&
    css`
      grid-column: span 2;
      grid-row: span 2;
    `}
`;

const BackgroundImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.2s ease-in-out;

  ${Wrapper}:hover & {
    transform: scale(1.04);
  }
`;

const Description = styled.div`
  color: ${(props) => props.theme.primary4};
  margin: 0;
  min-height: 50px;
  padding: 0 10px 5px;
  display: grid;
  column-gap: 5px;
  align-content: flex-end;
  background: linear-gradient(to bottom, rgba(3, 3, 3, 0) 0%, rgba(3, 3, 3, 0.4) 34%, rgba(3, 3, 3, 0.9) 100%);
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  transition: opacity 0.2s ease-in-out;
  grid-template-columns: minmax(auto, min-content) 1fr;
  grid-template-rows: repeat(2, minmax(0, min-content));
  grid-template-areas:
    'title title'
    'model-name media-count';

  @media ${DESKTOP} {
    opacity: 0;
    grid-template-areas:
      'title media-count'
      'model-name model-name';

    ${Wrapper}:hover & {
      opacity: 1;
    }
  }
`;

const Title = styled.div`
  grid-area: title;
  font-size: 14px;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  @media ${DESKTOP} {
    font-size: 18px;
  }

  @media ${XX_LARGE} {
    font-size: 32px;
  }
`;

const ModelName = styled.div`
  grid-area: model-name;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  margin-right: auto;
  font-size: 8px;
  white-space: nowrap;

  @media ${DESKTOP} {
    font-size: 12px;
  }

  @media ${XX_LARGE} {
    font-size: 24px;
  }
`;

const MediaCount = styled.div`
  grid-area: media-count;
  font-size: 8px;
  align-content: flex-end;
  padding-bottom: 3px;
  text-align: right;
  white-space: nowrap;

  @media ${DESKTOP} {
    text-align: left;
    font-size: 12px;
  }

  @media ${XX_LARGE} {
    font-size: 20px;
  }
`;

const StyledIcon = styled(FontAwesomeIcon)`
  margin-left: 0.25em;
`;
